import React from "react"
import { Avatar, Box, Grid, Tooltip, Typography, useTheme } from "@material-ui/core"
import { useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import moment from "moment"
import useStyles from "./styles"
import { REVIEW_DETAIL_DATE_FORMAT, convertFilterToObject, convertFilterToString, getConceptsById, searchTermDeparse } from "../../../utils"
import ConceptList from "./ConceptList"

const ReviewMetadata = ({ selectedReview }) => {
	const classes = useStyles()
	const theme = useTheme()
	const [t] = useTranslation("global")

	const savedConcepts = useSelector((state) => state?.buildconcept?.user_saved_build_concepts_unshared) || []
	const sharedConcepts = useSelector((state) => state?.buildconcept?.user_saved_build_concepts_shared) || []
	const users = useSelector((state) => state.info?.usersList || [])
	const tags = useSelector((state) => state.tags?.custom_tags?.tags || [])

	const getOwnerNameById = (id) => {
		if (users?.length && id) {
			const user = users?.find((user) => user.user_id === id)
			if (!_.isEmpty(user)) {
				return user?.username
			} else {
				return ""
			}
		}

		return ""
	}

	function stringToColor(string) {
		let hash = 0
		let i
		for (i = 0; i < string.length; i += 1) {
			hash = string.charCodeAt(i) + ((hash << 5) - hash)
		}
		let color = "#"
		for (i = 0; i < 3; i += 1) {
			const value = (hash >> (i * 8)) & 0xff
			color += `00${value.toString(16)}`.slice(-2)
		}
		return color
	}

	var getContrast = function (hexcolor) {
		if (hexcolor.slice(0, 1) === "#") {
			hexcolor = hexcolor.slice(1)
		}
		var r = parseInt(hexcolor.substr(0, 2), 16)
		var g = parseInt(hexcolor.substr(2, 2), 16)
		var b = parseInt(hexcolor.substr(4, 2), 16)
		var yiq = (r * 299 + g * 587 + b * 114) / 1000
		return yiq >= 128 ? theme.palette.text.primary : theme.palette.background.default
	}

	const stringAvatar = (id) => {
		const name = getOwnerNameById(id)
		return {
			style: {
				...theme?.typography?.avtarText,
				background: stringToColor(name),
				color: getContrast(stringToColor(name)),
				width: "36px",
				height: "36px",
			},
			children: name?.split(" ")[1] ? `${name?.split(" ")[0][0]}${name?.split(" ")[1][0]}` : name?.split(" ")[0][0],
		}
	}

	//Search metadata
	const searchName = selectedReview?.search_criteria?.searchName || t("review.reviewForm.all_documents")
	const objective = selectedReview?.objective
	const searchKeywords = selectedReview?.id ? selectedReview?.search_criteria?.terms : currentSearch?.text || ""
	const searchConcepts = getConceptsById([...savedConcepts, ...sharedConcepts], selectedReview?.search_criteria?.concept_ids, true)
	const selectedfilters = convertFilterToString(selectedReview?.search_criteria?.filters)
	const dueDate = selectedReview?.due_date ? moment(selectedReview?.due_date).format(REVIEW_DETAIL_DATE_FORMAT) : ""
	const filters = convertFilterToObject(selectedReview?.search_criteria?.filters)

	return (
		<Box component="span" m={1} className={classes.metaDataRoot}>
			<Grid container>
				{/* Avatar/name*/}
				<Grid item xs={12} className={classes.paddingLR12}>
					<div className={`${classes.flex} ${classes.alignItemCentrer} ${classes.padding8}`}>
						<Avatar alt={"A"} {...stringAvatar(selectedReview?.owner_id)} className={classes.avatar} />
						<div className={`${classes.flex} ${classes.alignItemCentrer} ${classes.padding8}`}>
							<Typography className={classes.createdBy}>{t("review.reviewMetadata.createdBy")}</Typography>
							<Typography className={classes.ownerName}>{getOwnerNameById(selectedReview?.owner_id)}</Typography>
						</div>
					</div>
				</Grid>
				{/* Goal*/}
				<Grid item xs={12} className={`${classes.borderTop} ${classes.paddingLR12}`}>
					<Typography className={`${classes.reviewBased} ${classes.padding8}`}>{t("review.reviewMetadata.goal")}</Typography>
					<Typography className={`${classes.keyword} ${classes.padding8}`}>{objective}</Typography>
				</Grid>
				{/* review based on*/}
				<Grid item xs={12} className={`${classes.borderTop} ${classes.paddingLR12}`}>
					<Typography className={`${classes.reviewBased} ${classes.padding8}`}>{t("review.reviewMetadata.basedOn")}</Typography>
					<Typography className={`${classes.searchName} ${classes.padding8}`}>{searchName}</Typography>
				</Grid>
				{/* Keywords*/}
				{!_.isEmpty(searchKeywords) ? (
					<Grid item xs={12} className={`${classes.borderTop} ${classes.paddingLR12}`}>
						<Typography className={`${classes.heading} ${classes.padding8}`}>{t("review.reviewMetadata.keywords")}</Typography>
						{searchKeywords?.map((keyword) => (
							<Typography key={keyword} className={`${classes.keyword} ${classes.padding8}`}>
								{keyword}
							</Typography>
						))}
					</Grid>
				) : (
					<></>
				)}

				{/* concepts*/}
				{!_.isEmpty(searchConcepts) ? (
					<Grid item xs={12} className={`${classes.borderTop}`}>
						<Typography className={`${classes.heading} ${classes.padding8} ${classes.paddingLR12}`}>
							{t("review.reviewMetadata.concepts")}
						</Typography>
						<ConceptList concepts={searchConcepts} />
					</Grid>
				) : (
					<></>
				)}

				{/* filter*/}
				{!_.isEmpty(selectedfilters) ? (
					<Grid item xs={12} className={`${classes.borderTop} ${classes.paddingLR12}`}>
						<Typography className={`${classes.heading} ${classes.padding8}`}>{t("review.reviewMetadata.filters")}</Typography>

						{!_.isEmpty(filters) ? (
							Object.keys(filters).map((item) => {
								let value = filters[item]
								if (item === "Comments By") {
									value = users
										?.reduce((result, current) => {
											if (value?.includes(current?.user_id)) {
												result.push(current?.username)
											}
											return result
										}, [])
										?.join(", ")
								} else if (item === "Tags") {
									value = tags
										?.reduce((result, current) => {
											if (value?.includes(current?.id)) {
												result.push(current?.name)
											}
											return result
										}, [])
										?.join(", ")
								}

								return (
									<div className={`${classes.flex} ${classes.padding8}`}>
										<Typography className={classes.filtertype}>{item}</Typography>
										<Tooltip title={value} placeholder="top" arrow>
											<Typography className={classes.filterData}>{value}</Typography>
										</Tooltip>
									</div>
								)
							})
						) : (
							<></>
						)}
					</Grid>
				) : (
					<></>
				)}

				{/* due date*/}
				<Grid item xs={12} className={`${classes.borderTop} ${classes.paddingLR12}`}>
					<div className={`${classes.flex} ${classes.padding8}`}>
						<Typography className={classes.dueDateText}>{t("review.reviewMetadata.dueDate")}</Typography>
						<Typography className={classes.dueDate}>{dueDate}</Typography>
					</div>
				</Grid>
			</Grid>
		</Box>
	)
}

export default ReviewMetadata
